import { fetchCallReadOnlyFunction } from '@stacks/transactions'
import { c32addressDecode } from 'c32check'

/**
 * Recursively converts @stacks/transactions v7 ClarityValue structure
 * to the v6 format expected by the rest of the codebase.
 *
 * Key differences:
 *   v7 tuple  → { type: 'tuple', value: { field: cv } }
 *   v6 tuple  → { type: 12,      data:  { field: cv } }
 *
 *   v7 list   → { type: 'list', value: [...] }
 *   v6 list   → { type: 11,     list:  [...] }
 *
 *   v7 uint   → { type: 'uint', value: 123n }        (BigInt)
 *   v6 uint   → { type: 1,      value: '123' }       (string)
 *
 *   v7 bool   → { type: 'true' } | { type: 'false' }
 *   v6 bool   → { type: 3 }      | { type: 4 }
 *
 *   v7 addr   → { type: 'address', value: 'SP...' }
 *   v6 addr   → { type: 5, address: { type, hash160 } }
 */
const convertCV = (cv: any): any => {
  if (!cv || typeof cv !== 'object') return cv

  switch (cv.type) {
    case 'uint':
      return { type: 1, value: cv.value.toString() }

    case 'int':
      return { type: 0, value: cv.value.toString() }

    case 'true':
      return { type: 3 }

    case 'false':
      return { type: 4 }

    case 'none':
      return { type: 9 }

    case 'some':
      return { type: 10, value: convertCV(cv.value) }

    case 'ok':
      return { type: 7, value: convertCV(cv.value) }

    case 'err':
      return { type: 8, value: convertCV(cv.value) }

    case 'list':
      return { type: 11, list: (cv.value as any[]).map(convertCV) }

    case 'tuple': {
      const data: Record<string, any> = {}
      for (const [key, val] of Object.entries(cv.value as Record<string, any>)) {
        data[key] = convertCV(val)
      }
      return { type: 12, data }
    }

    case 'address': {
      try {
        const [version, hash160] = c32addressDecode(cv.value)
        return { type: 5, address: { type: version, hash160 } }
      } catch {
        return { type: 5, address: { hash160: '' }, value: cv.value }
      }
    }

    case 'contract_principal': {
      try {
        const [fullAddress, contractName] = cv.value.split('.')
        const [version, hash160] = c32addressDecode(fullAddress)
        return {
          type: 6,
          address: { type: version, hash160 },
          contractName: { type: 2, content: contractName }
        }
      } catch {
        return cv
      }
    }

    case 'buffer':
      return { type: 2, buffer: cv.value }

    case 'ascii':
      return { type: 13, data: cv.value }

    case 'utf8':
      return { type: 14, data: cv.value }

    default:
      return cv
  }
}

export const fetchRetry = async (url: string, options: RequestInit = {}, retry: number = 10, timeout: number = 10000): Promise<any> => {
  const handleErr = async (err: any) => {
    const remainingAttempts = retry - 1

    if (!remainingAttempts) {
      throw `Failed to fetch from URL '${url}': ${err instanceof Error ? err.message : String(err)}`
    }

    await promiseWait(timeout)

    return await fetchRetry(url, options, remainingAttempts, timeout * 2)
  }

  try {
    const res = await fetch(url, options)

    return res
  } catch (err) {
    return handleErr(err)
  }
}

export const readOnlyRetry = async (options: any, retry: number = 10, timeout: number = 10000): Promise<any> => {
  const handleErr = async (err: any) => {
    const remainingAttempts = retry - 1

    if (!remainingAttempts) {
      throw `Failed to fetch from read-only function '${options.functionName}': ${err instanceof Error ? err.message : String(err)}`
    }

    await promiseWait(timeout)

    return await readOnlyRetry(options, remainingAttempts, timeout * 2)
  }

  try {
    const result = await fetchCallReadOnlyFunction(options)
    return convertCV(result)
  } catch (err) {
    return handleErr(err)
  }
}

export const promiseWait = (timeout: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, timeout))
}
